import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {outputFromObservable} from '@angular/core/rxjs-interop';
import {Observable, Subject} from 'rxjs';
import {UntypedFormControl} from '@angular/forms';
import {OverlayModule, ScrollStrategy, ScrollStrategyOptions} from '@angular/cdk/overlay';
import {MatFormFieldAppearance, MatFormFieldModule, SubscriptSizing} from '@angular/material/form-field';
import {ThemePalette} from '@angular/material/core';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatDivider} from '@angular/material/divider';
import {ReactiveFormsModule} from '@angular/forms';
import {NgTemplateOutlet} from '@angular/common';
import {SearchBoxDirective} from '../../directives/searchbox/searchbox.directive';
import {PermissionRequiredDirective} from '../../directives-core/permission-required.directive';

@Component({
    selector: 'xui-mat-autocomplete',
    templateUrl: './app-mat-autocomplete.component.html',
    styleUrls: ['./app-mat-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
      MatCard, MatCardContent, MatDivider, OverlayModule,
      ReactiveFormsModule, NgTemplateOutlet, SearchBoxDirective, PermissionRequiredDirective
    ]
})
export class AppMatAutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {

  private scrollStrategyOptions = inject(ScrollStrategyOptions);
  private cdr = inject(ChangeDetectorRef);

  autocompleteControl = new UntypedFormControl('');
  errorText = '';
  isSearching = false;
  searchResults: any[] = null;
  scrollStrategy: ScrollStrategy = this.scrollStrategyOptions.block();
  hostWidth: number | string = '';
  offsetY = 16;

  readonly appearance = input<MatFormFieldAppearance>('fill');
  readonly color = input<ThemePalette>('primary');
  readonly placeholder = input('Search');
  readonly searchObservable = input<((query: string) => Observable<any[]>) | null>(null);
  readonly addItemCardText = input('Add new item');
  readonly displayResultsCardTemplate = input<TemplateRef<any> | null>(null);
  readonly alwaysShowAddCard = input(false);
  readonly disabled = input(false);
  readonly isInvalid = input(false);
  readonly compact = input(false);
  readonly subscriptSizing = input<SubscriptSizing>('dynamic');
  private readonly addNewItemSubject = new Subject<string>();
  readonly addNewItem = outputFromObservable(this.addNewItemSubject);
  private readonly itemSelectedSubject = new Subject<any>();
  readonly itemSelected = outputFromObservable(this.itemSelectedSubject);
  @ViewChild('host') hostElement: ElementRef;

  get queryText(): string {
    return this.autocompleteControl.value ?? '';
  }

  get resultsReady(): boolean {
    return !this.isSearching && this.searchResults !== null && this.searchResults.length >= 0 && (this.autocompleteControl.value?.length ?? 0) > 0;
  }

  get hasError(): boolean {
    return this.errorText.length > 0;
  }

  get canAddNewItem(): boolean {
    return this.addNewItemSubject.observed;
  }

  ngOnInit(): void {
    if (this.searchObservable() === null) {
      throw Error('SearchObservable is a required input');
    }
    if (this.displayResultsCardTemplate() === null) {
      throw Error('displayResultsCardTemplate is a required input');
    }
  }

  ngAfterViewInit(): void {
    this.hostWidth = this.hostElement?.nativeElement?.offsetWidth || '';
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
  }

  search(value: string) {
    this.autocompleteControl.setValue(value);
  }

  selectItem(result: any) {
    this.itemSelectedSubject.next(result);
    this.cancelSearch();
  }

  onCardClick(event: MouseEvent, result: any) {
    const target = event.target as HTMLElement | null;
    if (target && target.closest('input, textarea, select, button, label, .mat-mdc-form-field, .mdc-text-field')) {
      // Allow interaction with form controls inside result templates without
      // triggering whole-card selection (which would close the search panel).
      return;
    }
    event.stopPropagation();
    this.selectItem(result);
  }

  addItem() {
    this.addNewItemSubject.next(this.queryText);
  }

  cancelSearch() {
    this.autocompleteControl.setValue('');
    this.errorText = '';
    this.searchResults = null;
  }

  closeSearchResults(selectedValue: any) {
    this.autocompleteControl.setValue(selectedValue);
    this.errorText = '';
    this.searchResults = null;
  }

  onSearchResults(results: any[]) {
    if (results === null) {
      this.searchResults = [];
    }
    this.searchResults = results;
    this.errorText = '';
  }

  onSearchStatusChange(isSearching: boolean) {
    this.isSearching = isSearching;
  }

  onError(errorMessage: string) {
    this.errorText = errorMessage;
  }
}
